import { useNavigate, useSearchParams } from "react-router-dom";
import GridView from "../../components/controls/GridView/GridView";
import roleService, { Role } from "../../services/roleService";
import { MenuItem } from "../../models/menu";
import { useContext, useEffect, useState } from "react";
import TransactionLimit from "../../components/controls/TransactionLimit/TransactionLimit";
import FormModal from "../../components/controls/FormModal/FormModal";
import ComboBox from "../../components/controls/ComboBox";
import FormCommand from "../../components/controls/FormCommand";
import { RoleTransactionLimit } from "../../models/interfaces";
import Frame from "../../components/controls/Frame/Frame";
import { AlertContext } from "../../components/controls/MessageBox/MessageBox";
import ContextMenu from "../../components/controls/ContextMenu/ContextMenu";

export interface RoleListingPageProps {}

export const RoleListingPage = (props: RoleListingPageProps) => {
  const [showTransactionLimit, setShowTransactionLimit] = useState(false);
  const [role, setRole] = useState<Role>();
  const [roles, setRoles] = useState<Role[]>([]);
  const { showAlert } = useContext(AlertContext);

  const navigate = useNavigate();

  function getRoleContextMenu(): MenuItem<Role>[] {
    const customerMenu: MenuItem<Role>[] = [
      new MenuItem("Edit role", function (e: Role) {
        navigate(`../role?id=${e.id}`, { relative: "path" });
      }),
      new MenuItem("Delete role", function (e: Role) {
        const ans = window.confirm(
          "Are you sure you want to delete this role\n" + e.name
        );
        if (ans) {
          const deleteRole = async () => {
            const res = await roleService.deleteRole(e.id ?? 0);
            if (res.status) {
              window.alert(res.message);
            }
          };

          deleteRole();
        }
      }),
      new MenuItem("Set Transaction Limit", function (e: Role) {
        setShowTransactionLimit(true);
        setRole(e);
      }),
    ];

    return customerMenu;
  }

  return (
    <>
      <div className="row">
        <div className="col">
          <Frame>
            <h4>Roles</h4>
            <GridView
              showAddButton={true}
              showSearchBar={true}
              keys={["name", "id"]}
              name={"lstRoles"}
              onAddRecord={() => setRole({})}
              onSearchClick={() => {
                console.log("Search button clicked!");
              }}
            >
              <GridView.Header>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
              </GridView.Header>
              <GridView.Content>
                {roles.map((d) => (
                  <tr>
                    <td>{d.name}</td>
                    <td>{d.description}</td>
                    <td>
                      <ContextMenu menu={getRoleContextMenu()} item={d} />
                    </td>
                  </tr>
                ))}
              </GridView.Content>
            </GridView>

            {showTransactionLimit && (
              <FormModal onClose={() => setShowTransactionLimit(false)}>
                <TransactionLimit<RoleTransactionLimit>
                  displayTitle={() => {
                    return (
                      <h5>
                        <b>{role?.name}</b>
                      </h5>
                    );
                  }}
                  onGetTransactionLimits={async () => {
                    return roleService.getRoleTransactionLimits(role?.id ?? 0);
                  }}
                  onSaveChanges={async (limit) => {
                    const res = await roleService.setRoleTransactionLimit(
                      role?.id ?? 0,
                      {
                        amount: limit.amount,
                        period: limit.period,
                        transactionType: limit.transactionType,
                      }
                    );

                    return res.data;
                  }}
                  onDeleteLimit={async (limit, limits) => {
                    const res = await roleService.deleteRoleLimit({
                      roleId: role?.id ?? 0,
                      period: Number(limit.periodType),
                      transactionType: limit.transactionTypeCode,
                    });

                    if (res.status) {
                      const newRoleLimits = limits.filter(
                        (d) =>
                          !(
                            d.transactionTypeCode ===
                              limit.transactionTypeCode &&
                            d.periodType === limit.periodType &&
                            d.roleId === limit.roleId
                          )
                      );

                      return newRoleLimits;
                    }
                  }}
                />
              </FormModal>
            )}
          </Frame>
        </div>
      </div>

      {role && (
        <FormModal
          title={role?.id == 0 ? "Create Role" : "Edit Role"}
          onClose={() => setRole(undefined)}
        >
          <RoleForm />
        </FormModal>
      )}
    </>
  );
};

export interface RoleFormProps {}

export function RoleForm(props: RoleFormProps) {
  const navigate = useNavigate();
  const [roleName, setRoleName] = useState("");
  const [transactionLimit, setTransactionLimit] = useState<number>(0);

  const [params] = useSearchParams();
  const roleId = Number(params.get("id"));

  const saveRole = async () => {
    const res = await roleService.saveRole(
      {
        name: roleName,
        transactionLimit: transactionLimit,
      },
      roleId
    );

    if (res.status) {
      setTransactionLimit(0);
      setRoleName("");
    }
  };

  useEffect(() => {
    if (roleId !== 0) {
      const getRole = async () => {
        const res = await roleService.getRole(roleId);
        if (res.status) {
          setRoleName(res.data?.name ?? "");
          setTransactionLimit(res.data?.transactionLimit ?? 0);
        }
      };

      getRole();
    }
  }, [roleId]);

  return (
    <div className="row">
      <div className="col">
        <div className="form-group">
          <label htmlFor="roleName" className="control-label">
            Name
          </label>
          <input
            type="text"
            name="roleName"
            id="roleName"
            className="form-control form-control-sm"
            onChange={(e) => setRoleName(e.currentTarget.value)}
            value={roleName}
          />
        </div>

        <div className="row">
          <div className="col">
            <div className="form-group">
              <label htmlFor="transactionLimit" className="control-label">
                Transaction Limit
              </label>
              <input
                type="number"
                name="transactionLimit"
                id="transactionLimit"
                className="form-control form-control-sm"
                onChange={(e) => {
                  setTransactionLimit(Number(e.currentTarget.value));
                }}
                value={transactionLimit}
              />
            </div>
          </div>
          <div className="col">
            <ComboBox
              id="lstTransactionLimitPeriod"
              text="Limit Period"
              prompt="Select Period"
              items={[
                { id: 1, name: "Daily" },
                { id: 2, name: "Weekly" },
                { id: 3, name: "Monthly" },
              ]}
            />
          </div>
        </div>

        <FormCommand
          onSubmit={() => {
            saveRole();
          }}
        />
      </div>
    </div>
  );
}
